/**
 * ChartOfAccountsScreen — Tree view matching mockup exactly
 * Hierarchy: Assets → Liabilities → BS Totals → Incomes → Expenses → PL Totals
 * Columns: Account Description | Opening Debit | Opening Credit | Debit Balance | Credit Balance | Closing Balance
 * Click GL → opens voucher popup in edit mode
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronRight, ChevronDown, Download, RefreshCw, Search, MoreVertical, ArrowLeft } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import VoucherScreen from './VoucherScreen';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API, getToken} from '../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });
const fmtC = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const ChartOfAccountsScreen = ({ theme = 'light', navigateTo = () => {} }) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set(['all']));
  const [totals, setTotals] = useState({ bsDr: 0, bsCr: 0, plDr: 0, plCr: 0 });
  const [voucherPopup, setVoucherPopup] = useState(null);
  const [voucherData, setVoucherData] = useState([]);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [vPage, setVPage] = useState(1);
  const [vPageSize, setVPageSize] = useState(10);
  const [vSortField, setVSortField] = useState('');
  const [vSortDir, setVSortDir] = useState('desc');
  const [vFilters, setVFilters] = useState({});
  const [vFromDate, setVFromDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [vToDate, setVToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [vSelectedRow, setVSelectedRow] = useState(null);
  const [editLedgerId, setEditLedgerId] = useState(null);
  const [voucherEdit, setVoucherEdit] = useState(null); // { transType, docId }
  const [searchText, setSearchText] = useState('');
  const [showZeros, setShowZeros] = useState('without');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [glContextMenu, setGlContextMenu] = useState(null);
  const exportRef = useRef(null);
  const ctxRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/financial-statements/financial-statement?category=all`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        setTree(j.data.tree || []);
        // Compute BS / PL totals
        let bsDr = 0, bsCr = 0, plDr = 0, plCr = 0;
        (j.data.tree || []).forEach(sch => (sch.children || []).forEach(pg => {
          if (pg.category === 'BS') { bsDr += pg.drOpen + pg.drCurrent; bsCr += pg.crOpen + pg.crCurrent; }
          else { plDr += pg.drOpen + pg.drCurrent; plCr += pg.crOpen + pg.crCurrent; }
        }));
        setTotals({ bsDr, bsCr, plDr, plCr });
      }
    } catch { erpToast.error('Failed to load Chart of Accounts'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Open voucher popup — skip if zero transaction
  const openVoucherPopup = async (glId, glName, drTotal, crTotal) => {
    if ((drTotal || 0) === 0 && (crTotal || 0) === 0) return; // #4: zero = no popup
    setVoucherPopup({ glId, glName });
    setVoucherLoading(true);
    try {
      const r = await fetch(`${API}/api/finance-reports/gl-ledger?accountId=${glId}&fiscalYear=2025-2026`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setVoucherData(j.data.entries || []);
    } catch { setVoucherData([]); }
    setVoucherLoading(false);
  };

  // Close context menu on outside click
  useEffect(() => {
    if (!glContextMenu) return;
    const h = (e) => { if (ctxRef.current && !ctxRef.current.contains(e.target)) setGlContextMenu(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [glContextMenu]);

  // Export helpers
  const handleExport = (format) => {
    setShowExportMenu(false);
    const allPgs = tree.flatMap(s => (s.children || []).flatMap(pg => [pg, ...(pg.children || [])]));
    if (format === 'csv') {
      const csv = 'Account,OpenDr,OpenCr,DrBal,CrBal,Closing\n' + allPgs.map(p => `"${p.name}",${p.drOpen||0},${p.crOpen||0},${p.drCurrent||0},${p.crCurrent||0},${(p.drOpen||0)+(p.drCurrent||0)-(p.crOpen||0)-(p.crCurrent||0)}`).join('\n');
      const b = new Blob(['\ufeff' + csv], { type: 'text/csv' }); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'ChartOfAccounts.csv'; a.click();
    } else if (format === 'pdf') {
      window.print();
    } else if (format === 'excel') {
      const html = '<html><head><meta charset="utf-8"></head><body><table border="1">' + '<tr><th>Account</th><th>OpenDr</th><th>OpenCr</th><th>DrBal</th><th>CrBal</th><th>Closing</th></tr>' + allPgs.map(p => `<tr><td>${p.name}</td><td>${p.drOpen||0}</td><td>${p.crOpen||0}</td><td>${p.drCurrent||0}</td><td>${p.crCurrent||0}</td><td>${(p.drOpen||0)+(p.drCurrent||0)-(p.crOpen||0)-(p.crCurrent||0)}</td></tr>`).join('') + '</table></body></html>';
      const b = new Blob([html], { type: 'application/vnd.ms-excel' }); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'ChartOfAccounts.xls'; a.click();
    }
    erpToast.success(`Exported as ${format.toUpperCase()}`);
  };

  // Color scheme — matches GL Report blue theme
  const headerBg = '#e8f0fe';
  const headerFg = '#1e40af';
  const subBg = '#f1f5fb';
  const totalBg = '#3b5998';
  const totalFg = '#fff';

  const closingStr = (dr, cr) => { const diff = dr - cr; return fmtC(Math.abs(diff)) + (diff >= 0 ? ' Dr' : ' Cr'); };

  const renderRows = () => {
    const rows = [];
    const allSchChildren = tree.flatMap(sch => sch.children || []);
    const bsGroups = allSchChildren.filter(pg => pg.category === 'BS');
    const plGroups = allSchChildren.filter(pg => pg.category !== 'BS');

    // Group by PrimaryGroup name pattern for Assets/Liabilities/Income/Expenses
    const assets = bsGroups.filter(pg => (pg.name || '').match(/asset|invest|misc|expenditure/i));
    const liabilities = bsGroups.filter(pg => !(pg.name || '').match(/asset|invest|misc|expenditure/i));
    const incomes = plGroups.filter(pg => (pg.name || '').match(/income/i));
    const expenses = plGroups.filter(pg => !(pg.name || '').match(/income/i));

    const renderSection = (label, groups, bgColor) => {
      const secId = `sec-${label}`;
      const isExp = expanded.has(secId);
      const secDrOpen = groups.reduce((s, g) => s + (g.drOpen || 0), 0);
      const secCrOpen = groups.reduce((s, g) => s + (g.crOpen || 0), 0);
      const secDrCur = groups.reduce((s, g) => s + (g.drCurrent || 0), 0);
      const secCrCur = groups.reduce((s, g) => s + (g.crCurrent || 0), 0);

      rows.push(
        <tr key={secId} style={{ background: bgColor, color: bgColor === headerBg ? headerFg : '#333', cursor: 'pointer', fontWeight: 700 }} onClick={() => toggle(secId)}>
          <td style={{ padding: '8px 12px', fontSize: 14 }}>
            {isExp ? <ChevronDown size={14} style={{ display: 'inline', marginRight: 6 }} /> : <ChevronRight size={14} style={{ display: 'inline', marginRight: 6 }} />}
            {label}
          </td>
          <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px', fontSize: 13 }}>{fmtC(secDrOpen)}</td>
          <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px', fontSize: 13 }}>{fmtC(secCrOpen)}</td>
          <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px', fontSize: 13 }}>{fmtC(secDrCur)}</td>
          <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px', fontSize: 13 }}>{fmtC(secCrCur)}</td>
          <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px', fontWeight: 700, fontSize: 13 }}>{closingStr(secDrOpen + secDrCur, secCrOpen + secCrCur)}</td>
        </tr>
      );

      if (!isExp) return;
      for (const pg of groups) {
        const pgId = `pg-${pg.id}`;
        const pgExp = expanded.has(pgId);
        rows.push(
          <tr key={pgId} style={{ background: subBg, cursor: 'pointer' }} onClick={() => toggle(pgId)}>
            <td style={{ padding: '4px 12px 4px 32px', fontSize: 12, fontWeight: 600 }}>
              {pgExp ? <ChevronDown size={12} style={{ display: 'inline', marginRight: 4 }} /> : <ChevronRight size={12} style={{ display: 'inline', marginRight: 4 }} />}
              {pg.name}
            </td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '4px 8px' }}>{fmtC(pg.drOpen)}</td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '4px 8px' }}>{fmtC(pg.crOpen)}</td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '4px 8px' }}>{fmtC(pg.drCurrent)}</td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '4px 8px' }}>{fmtC(pg.crCurrent)}</td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '4px 8px' }}>{closingStr(pg.drOpen + pg.drCurrent, pg.crOpen + pg.crCurrent)}</td>
          </tr>
        );
        if (!pgExp) continue;
        for (const grp of pg.children || []) {
          const grpId = `grp-${grp.id}`;
          const grpExp = expanded.has(grpId);
          rows.push(
            <tr key={grpId} style={{ cursor: 'pointer' }} onClick={() => toggle(grpId)}>
              <td style={{ padding: '3px 12px 3px 52px', fontSize: 11, fontWeight: 500 }}>
                {grpExp ? <ChevronDown size={11} style={{ display: 'inline', marginRight: 4 }} /> : <ChevronRight size={11} style={{ display: 'inline', marginRight: 4 }} />}
                {grp.name} <span style={{ fontSize: 9, color: '#999' }}>({grp.ledgerCount})</span>
              </td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '3px 8px' }}>{fmtC(grp.drOpen)}</td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '3px 8px' }}>{fmtC(grp.crOpen)}</td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '3px 8px' }}>{fmtC(grp.drCurrent)}</td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '3px 8px' }}>{fmtC(grp.crCurrent)}</td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '3px 8px' }}>{closingStr(grp.drOpen + grp.drCurrent, grp.crOpen + grp.crCurrent)}</td>
            </tr>
          );
          if (!grpExp) continue;
          for (const gl of grp.children || []) {
            const glTotal = (gl.drOpen || 0) + (gl.drCurrent || 0) + (gl.crOpen || 0) + (gl.crCurrent || 0);
            const isZero = glTotal === 0;
            if (showZeros === 'without' && isZero) continue;
            if (searchText && !(gl.name || '').toLowerCase().includes(searchText.toLowerCase()) && !(gl.id || '').toLowerCase().includes(searchText.toLowerCase())) continue;
            rows.push(
              <tr key={`gl-${gl.id}`} style={{ cursor: isZero ? 'default' : 'pointer' }} data-testid={`coa-gl-${gl.id}`}>
                <td style={{ padding: '2px 12px 2px 60px', fontSize: 11, color: isZero ? 'var(--erp-text-muted)' : 'var(--erp-primary)', textDecoration: isZero ? 'none' : 'underline' }}
                  onClick={() => openVoucherPopup(gl.id, gl.name, (gl.drOpen||0)+(gl.drCurrent||0), (gl.crOpen||0)+(gl.crCurrent||0))}>
                  {gl.name}
                  {!isZero && <button onClick={(e) => { e.stopPropagation(); setGlContextMenu({ id: gl.id, name: gl.name, drTotal: (gl.drOpen||0)+(gl.drCurrent||0), crTotal: (gl.crOpen||0)+(gl.crCurrent||0), x: e.clientX, y: e.clientY }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 6px', color: '#888', marginLeft: 8, verticalAlign: 'middle' }} data-testid={`coa-ctx-${gl.id}`}><MoreVertical size={12} /></button>}
                </td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '2px 8px' }}>{fmtC(gl.drOpen)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '2px 8px' }}>{fmtC(gl.crOpen)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '2px 8px' }}>{fmtC(gl.drCurrent)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '2px 8px' }}>{fmtC(gl.crCurrent)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, padding: '2px 8px' }}>{closingStr(gl.drOpen + gl.drCurrent, gl.crOpen + gl.crCurrent)}</td>
              </tr>
            );
          }
        }
      }
    };

    renderSection('Assets', assets, headerBg);
    renderSection('Liabilities', liabilities, headerBg);
    // BS Totals row
    rows.push(
      <tr key="bs-totals" style={{ background: totalBg, color: totalFg, fontWeight: 700 }}>
        <td style={{ padding: '8px 12px', fontSize: 14, fontStyle: 'italic' }}>Balance Sheet Totals</td>
        <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px' }}>{fmtC(totals.bsDr)}</td>
        <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px' }}>{fmtC(totals.bsCr)}</td>
        <td colSpan={2} />
        <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px', fontWeight: 700 }}>{closingStr(totals.bsDr, totals.bsCr)}</td>
      </tr>
    );
    renderSection('Incomes', incomes, headerBg);
    renderSection('Expenses', expenses, headerBg);
    rows.push(
      <tr key="pl-totals" style={{ background: totalBg, color: totalFg, fontWeight: 700 }}>
        <td style={{ padding: '8px 12px', fontSize: 14, fontStyle: 'italic' }}>Profit & Loss Totals</td>
        <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px' }}>{fmtC(totals.plDr)}</td>
        <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px' }}>{fmtC(totals.plCr)}</td>
        <td colSpan={2} />
        <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', padding: '8px', fontWeight: 700 }}>{closingStr(totals.plDr, totals.plCr)}</td>
      </tr>
    );
    return rows;
  };

  return (
    <div className="erp-module" data-testid="chart-of-accounts">
      <div className="erp-screen-header">
        <button onClick={() => navigateTo('HUB')} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Chart of Accounts</span></button>
        <div className="erp-screen-actions" style={{ gap: 6 }}>
          {/* #2: Global search */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={12} style={{ position: 'absolute', left: 8, color: 'var(--erp-text-muted)' }} />
            <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search accounts..." className="erp-input" style={{ paddingLeft: 26, width: 180, height: 26, fontSize: 11 }} data-testid="coa-search" />
          </div>
          {/* #3: With/Without zeros */}
          <select value={showZeros} onChange={e => setShowZeros(e.target.value)} className="erp-input erp-select" style={{ width: 140, height: 26, fontSize: 11 }} data-testid="coa-filter">
            <option value="without">Without Zeros</option>
            <option value="with">With Zeros</option>
          </select>
          <button className="erp-btn erp-btn-ghost" onClick={loadData}><RefreshCw size={13} /></button>
          {/* #1: Single export button with dropdown */}
          <div style={{ position: 'relative' }} ref={exportRef}>
            <button className="erp-btn erp-btn-ghost" onClick={() => setShowExportMenu(!showExportMenu)} data-testid="coa-export-btn"><Download size={13} /> Export</button>
            {showExportMenu && (
              <div className="erp-action-dropdown" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: 'var(--erp-surface)', border: '1px solid var(--erp-border)', borderRadius: 'var(--erp-radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 140 }}>
                <button className="erp-action-item" onClick={() => handleExport('csv')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12 }}>Export as CSV</button>
                <button className="erp-action-item" onClick={() => handleExport('excel')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12 }}>Export as Excel</button>
                <button className="erp-action-item" onClick={() => handleExport('pdf')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12 }}>Export as PDF</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="erp-grid-wrapper" style={{ flex: 1, margin: 0, borderRadius: 0 }}>
        <div className="erp-grid-scroll">
          <table className="erp-grid" data-testid="coa-grid" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#4A6FA5', color: '#fff' }}>
                <th style={{ textAlign: 'left', minWidth: 300, padding: '8px 12px' }}>Account Description</th>
                <th style={{ textAlign: 'right', width: 140, padding: '8px' }}>Opening Debit</th>
                <th style={{ textAlign: 'right', width: 140, padding: '8px' }}>Opening Credit</th>
                <th style={{ textAlign: 'right', width: 140, padding: '8px' }}>Debit Balance</th>
                <th style={{ textAlign: 'right', width: 140, padding: '8px' }}>Credit Balance</th>
                <th style={{ textAlign: 'right', width: 160, padding: '8px' }}>Closing Balance</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading Chart of Accounts...</td></tr>
              : tree.length === 0 ? <tr><td colSpan={6} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>No data</td></tr>
              : renderRows()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Voucher Details Popup — ERPAdvancedGrid with context menu */}
      {voucherPopup && (
        <div className="erp-modal-overlay" data-testid="voucher-drill-modal">
          <div className="erp-modal" style={{ width: 960, height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="erp-modal-header">
              <span className="erp-modal-title">Voucher Details — {voucherPopup.glName}</span>
              <span style={{ fontSize: 11, color: 'var(--erp-text-muted)', marginLeft: 8 }}>({voucherPopup.glId})</span>
              <button className="erp-modal-close" onClick={() => { setVoucherPopup(null); setVPage(1); setVFilters({}); }}>X</button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <ERPAdvancedGrid
                title=""
                columns={[
                  { field: 'TransDateStr', header: 'Date' },
                  { field: 'TransType', header: 'Type' },
                  { field: 'TransNo', header: 'Voucher No' },
                  { field: 'AccountName', header: 'Account' },
                  { field: 'DrAmount', header: 'Debit' },
                  { field: 'CrAmount', header: 'Credit' },
                  { field: 'Balance', header: 'Balance' },
                  { field: 'DocRef', header: 'DocRef' },
                ]}
                data={(() => {
                  let items = [...voucherData];
                  if (vFromDate) items = items.filter(r => r.TransDateStr >= vFromDate);
                  if (vToDate) items = items.filter(r => r.TransDateStr <= vToDate);
                  Object.entries(vFilters).forEach(([k, v]) => { if (v) items = items.filter(r => String(r[k] || '').toLowerCase().includes(v.toLowerCase())); });
                  if (vSortField) items.sort((a, b) => { const av = String(a[vSortField] || ''), bv = String(b[vSortField] || ''); return vSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
                  return items.slice((vPage - 1) * vPageSize, vPage * vPageSize);
                })()}
                total={voucherData.length}
                idField="TransNo"
                nameField="AccountName"
                page={vPage}
                pageSize={vPageSize}
                onPageChange={setVPage}
                onPageSizeChange={(s) => { setVPageSize(s); setVPage(1); }}
                sortField={vSortField}
                sortDir={vSortDir}
                onSort={(f) => { if (vSortField === f) setVSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setVSortField(f); setVSortDir('asc'); } setVPage(1); }}
                filters={vFilters}
                onFilterChange={(f) => { setVFilters(f); setVPage(1); }}
                loading={voucherLoading}
                showAddNew={false}
                testId="voucher-drill"
                exportFileName={`Vouchers_${voucherPopup.glId}`}
                headerActions={<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ fontSize: 10, color: '#6b7280' }}>From</span><input type="date" value={vFromDate} onChange={e => { setVFromDate(e.target.value); setVPage(1); }} className="erp-input" style={{ height: 24, fontSize: 10, width: 115 }} /><span style={{ fontSize: 10, color: '#6b7280' }}>To</span><input type="date" value={vToDate} onChange={e => { setVToDate(e.target.value); setVPage(1); }} className="erp-input" style={{ height: 24, fontSize: 10, width: 115 }} /></div>}
                onEdit={(row, mode) => {
                  const typeMap = { CR: 'cash-receipts', CP: 'cash-payments', BR: 'bank-receipts', BP: 'bank-payments', JV: 'journal-vouchers', PV: 'cash-payments', SV: 'cash-receipts', DN: 'journal-vouchers', CN: 'journal-vouchers' };
                  setVoucherEdit({ voucherType: typeMap[row.TransType] || 'cash-receipts', docId: row.TransNo, mode: mode || 'view' });
                }}
                onRowClick={(row) => setVSelectedRow(row.TransNo)}
                selectedRowId={vSelectedRow}
                cellRenderer={(field, value) => {
                  if (field === 'DrAmount' && value > 0) return <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{fmtC(value)}</span>;
                  if (field === 'CrAmount' && value > 0) return <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{fmtC(value)}</span>;
                  if (field === 'DrAmount' || field === 'CrAmount') return <span />;
                  return null;
                }}
                cellClassName={(field) => ['DrAmount', 'CrAmount', 'Balance'].includes(field) ? 'text-right' : ''}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Ledger — info toast (stays in same session) */}
      {editLedgerId && (
        <div className="erp-modal-overlay" data-testid="edit-ledger-modal">
          <div className="erp-modal" style={{ width: 960, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="erp-modal-header">
              <span className="erp-modal-title">Edit General Ledger — {editLedgerId}</span>
              <button className="erp-modal-close" onClick={() => { setEditLedgerId(null); loadData(); }}>X</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 16, textAlign: 'center', color: 'var(--erp-text-muted)' }}>
              <p>Navigate to Financial Accounting → General Ledger to edit this account.</p>
              <p style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 13, color: 'var(--erp-primary)', marginTop: 8 }}>{editLedgerId}</p>
              <button className="erp-btn erp-btn-primary" onClick={() => setEditLedgerId(null)} style={{ marginTop: 16 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* #5: GL Context Menu — View/Edit/Reprint/Export */}
      {glContextMenu && (
        <div ref={ctxRef} style={{ position: 'fixed', top: glContextMenu.y, left: Math.min(glContextMenu.x, window.innerWidth - 180), zIndex: 200, background: '#fff', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.18)', minWidth: 170, overflow: 'hidden' }} data-testid="gl-context-menu">
          {[
            { label: 'View Vouchers', action: () => openVoucherPopup(glContextMenu.id, glContextMenu.name, glContextMenu.drTotal, glContextMenu.crTotal) },
            { label: 'Edit Ledger', action: () => setEditLedgerId(glContextMenu.id) },
            { label: 'Reprint', action: () => window.print() },
            { label: 'Export CSV', action: () => handleExport('csv') },
            { label: 'Export Excel', action: () => handleExport('excel') },
          ].map((item, i) => (
            <div key={i} onClick={() => { item.action(); setGlContextMenu(null); }}
              style={{ padding: '8px 16px', fontSize: 12, cursor: 'pointer', borderBottom: i < 4 ? '1px solid #f0f0f0' : 'none' }}
              onMouseEnter={(e) => { if (!e.currentTarget) return; e.currentTarget.style.background = '#f0f5fa'; }}
              onMouseLeave={(e) => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}
              data-testid={`gl-ctx-${item.label.replace(/ /g, '-').toLowerCase()}`}
            >{item.label}</div>
          ))}
        </div>
      )}

      {/* Voucher View/Edit — in-page modal (RULE 32: no window.open) */}
      {voucherEdit && (
        <div className="erp-modal-overlay" style={{ zIndex: 1300 }} data-testid="voucher-edit-modal">
          <div style={{ position: 'relative', width: '95vw', maxWidth: 1200, height: '90vh', background: 'var(--erp-surface)', borderRadius: 'var(--erp-radius)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-primary)', color: '#fff' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>Voucher — {voucherEdit.docId} ({voucherEdit.mode})</span>
              <button onClick={() => setVoucherEdit(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>X</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <VoucherScreen voucherType={voucherEdit.voucherType} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccountsScreen;
